/**
 * Replicação do seed de desenvolvimento do backend (DevelopmentDataSeeder) para mocks só em frontend.
 * Fonte normativa: `src/backend/Imoveis.Infrastructure/DevelopmentData/Seed` (ficheiros C# de seed).
 * Slugs públicos estáveis (deterministicKey); não usam o mesmo algoritmo C# do servidor.
 */
import { createMockLocalImageRow } from '@app/mock-api/mock-api-property-media-shape';
import type { MockCity, MockDistrict, MockImageRow, MockPropertyRecord } from '@app/mock-api/mock-api-shared-models';

/** Base Moneda — mesmo diretório que `DevelopmentDataSeeder.MonedaListingMedia.cs`. */
const MONEDA_IMAGE_2026_BASE = 'https://www.imobiliariamoneda.com.br/wp-content/uploads/2026/04/';

const MONEDA_CASA_PLANA_IMAGE_FILES = [
  'Image00029.jpg',
  'Image00028.jpg',
  'Image00001-2.jpg',
  'Image00026.jpg',
  'Image00024.jpg',
  'Image00025.jpg',
  'Image00022.jpg',
  'Image00020.jpg',
  'Image00021.jpg',
  'Image00019.jpg',
  'Image00017-1.jpg',
  'Image00023.jpg',
  'Image00016-1.jpg',
  'Image00018.jpg',
  'Image00015-1.jpg',
  'Image00002-2.jpg',
  'Image00014-1.jpg',
  'Image00011-1.jpg',
  'Image00009-1.jpg',
  'Image00012-1.jpg',
  'Image00010-1.jpg',
  'Image00008-1.jpg',
  'Image00007-1.jpg',
  'Image00005-2.jpg',
  'Image00013-1.jpg',
  'Image00004-2.jpg',
  'Image00006-1.jpg',
  'Image00027.jpg',
  'Image00003-2.jpg',
] as const;

const MONEDA_CASA_PLANA_IMAGE_URLS = MONEDA_CASA_PLANA_IMAGE_FILES.map((f) => MONEDA_IMAGE_2026_BASE + f);

/** Mesmas URLs que `DevelopmentDataSeeder.SeedGalleryImageLists.cs`. */
const SEED_SOBRADO_IMAGE_URLS = [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=80',
];

const SEED_APARTAMENTO_IMAGE_URLS = [
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?auto=format&fit=crop&w=1600&q=80',
];

const SEED_QUITINETE_IMAGE_URLS = [
  'https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1600&q=80',
];

const SEED_CHACARA_IMAGE_URLS = [
  'https://images.unsplash.com/photo-1449844908441-8829872d2607?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80',
];

const SEED_FAZENDA_IMAGE_URLS = [
  'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1600&q=80',
];

const SEED_TERRENO_IMAGE_URLS = [
  'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1600&q=80',
];

/** Mesma dupla que o backend; ordem: URL estável primeiro (evita hero com falha de carga no primeiro slot). */
const SEED_GLEBA_IMAGE_URLS = [
  'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=1600&q=80'
];

const SEED_DESCRIPTION_MONEDA =
  'Na rua da praça central. Todos os dormitórios com armários embutidos. Nos fundos, conta com mais uma edícula e área de serviço. Imóvel com charmosa varanda e excelente espaço plano de quintal. ' +
  'Dados do anúncio (referência Imobiliária Moneda, código 12179): Tipo CASA; bairro Centro; 3 dormitórios; 2 banheiros; 2 vagas; 130 m² de área construída; terreno 528 m²; preço de referência R$ 780.000,00.';

export const MOCK_SEED_CITIES: readonly MockCity[] = [
  { id: 'seed-city-aguas-da-prata', name: 'Águas da Prata', slug: 'aguas-da-prata-sp', state: 'SP' },
  { id: 'seed-city-sao-joao-da-boa-vista', name: 'São João da Boa Vista', slug: 'sao-joao-da-boa-vista-sp', state: 'SP' },
  { id: 'seed-city-pocos-de-caldas', name: 'Poços de Caldas', slug: 'pocos-de-caldas-mg', state: 'MG' },
];

/** Um bairro “Centro” por cidade — mesmo nome que o seed EF (`DevelopmentDataSeeder.cs`). */
export const MOCK_SEED_DISTRICTS_BY_CITY: Readonly<Record<string, MockDistrict[]>> = {
  'seed-city-aguas-da-prata': [
    {
      cityId: 'seed-city-aguas-da-prata',
      id: 'seed-dist-aguas-centro',
      name: 'Centro',
      slug: 'centro',
    },
  ],
  'seed-city-sao-joao-da-boa-vista': [
    {
      cityId: 'seed-city-sao-joao-da-boa-vista',
      id: 'seed-dist-sjbv-centro',
      name: 'Centro',
      slug: 'centro',
    },
  ],
  'seed-city-pocos-de-caldas': [
    {
      cityId: 'seed-city-pocos-de-caldas',
      id: 'seed-dist-pocos-centro',
      name: 'Centro',
      slug: 'centro',
    },
  ],
};

const UPDATED_AT_SEED = '2026-04-20T12:00:00.000Z';

function rowsFromUrls(urls: readonly string[]): MockImageRow[] {
  return urls.map((listingUrl, sortOrder) =>
    createMockLocalImageRow({
      id: `seed-img-${sortOrder}`,
      isPrimary: sortOrder === 0,
      listingUrl,
      sortOrder,
    }),
  );
}

interface SeedRowInput {
  bathroomCount: null | number;
  bedroomCount: null | number;
  description: string;
  deterministicKey: string;
  cityIndex: 0 | 1 | 2;
  imageUrls: readonly string[];
  parkingSpaceCount: null | number;
  postalCode: string;
  price: number;
  propertyType: number;
  referenceSuffix: string;
  street: string;
  title: string;
  totalAreaSqm: null | number;
}

function buildRecord(row: SeedRowInput): MockPropertyRecord {
  const city = MOCK_SEED_CITIES[row.cityIndex]!;
  const district = MOCK_SEED_DISTRICTS_BY_CITY[city.id]![0]!;
  const images = rowsFromUrls(row.imageUrls);
  const id = `seed-prop-${row.referenceSuffix}`;
  const digits = row.referenceSuffix.padStart(4, '0');

  return {
    bathroomCount: row.bathroomCount,
    bedroomCount: row.bedroomCount,
    cityId: city.id,
    complement: null,
    description: row.description,
    districtId: district.id,
    id,
    images,
    isActive: true,
    parkingSpaceCount: row.parkingSpaceCount,
    postalCode: row.postalCode.replace(/\D/g, ''),
    propertyType: row.propertyType,
    publicSlug: row.deterministicKey,
    referenceCode: `REF-2026-${digits}`,
    salePrice: row.price,
    state: city.state,
    streetLine: row.street,
    streetNumber: 'S/N',
    title: row.title,
    totalAreaSqm: row.totalAreaSqm,
    updatedAtUtc: UPDATED_AT_SEED,
  };
}

/** Mesmas 9 linhas que `BuildPropertyDefinitions()` no backend (ordem e dados de negócio). */
export function buildDevelopmentSeedPropertyRecords(): MockPropertyRecord[] {
  const rows: SeedRowInput[] = [
    {
      bathroomCount: 2,
      bedroomCount: 3,
      description: SEED_DESCRIPTION_MONEDA,
      deterministicKey: 'dev-seed-imo-moneda-casa-centro',
      cityIndex: 0,
      imageUrls: MONEDA_CASA_PLANA_IMAGE_URLS,
      parkingSpaceCount: 2,
      postalCode: '13890-000',
      price: 780_000,
      propertyType: 1,
      referenceSuffix: '0001',
      street: 'Rua da Praça Central',
      title: 'Casa plana e com excelente quintal no centro da cidade',
      totalAreaSqm: 130,
    },
    {
      bathroomCount: 3,
      bedroomCount: 4,
      description:
        'Sobrado em condomínio fechado, piso porcelanato, escada em granito, lavabo no térreo e espaço gourmet nos fundos. Documentação em ordem para financiamento.',
      deterministicKey: 'dev-seed-imo-02-sobrado',
      cityIndex: 1,
      imageUrls: SEED_SOBRADO_IMAGE_URLS,
      parkingSpaceCount: 2,
      postalCode: '13870-161',
      price: 920_000,
      propertyType: 2,
      referenceSuffix: '0002',
      street: 'Rua Doutor Waldomiro Cesar da Silveira',
      title: 'Sobrado moderno com sacada e área de lazer',
      totalAreaSqm: 198,
    },
    {
      bathroomCount: 2,
      bedroomCount: 3,
      description:
        'Apartamento andar intermediário, sol da manhã, living integrado com cozinha planejada, suíte com closed. Prédio com piscina, salão de festas e portaria 24h.',
      deterministicKey: 'dev-seed-imo-03-apto',
      cityIndex: 2,
      imageUrls: SEED_APARTAMENTO_IMAGE_URLS,
      parkingSpaceCount: 2,
      postalCode: '37701-351',
      price: 640_000,
      propertyType: 3,
      referenceSuffix: '0003',
      street: 'Rua Padre Américo Cepera',
      title: 'Apartamento com vista para a cidade e duas vagas',
      totalAreaSqm: 92,
    },
    {
      bathroomCount: 1,
      bedroomCount: 1,
      description:
        'Unidade térrea, cozinha americana, banheiro social reformado, gás encanado. Ideal para primeiro imóvel ou renda. Aceita proposta com entrada facilitada.',
      deterministicKey: 'dev-seed-imo-04-kitnet',
      cityIndex: 0,
      imageUrls: SEED_QUITINETE_IMAGE_URLS,
      parkingSpaceCount: 1,
      postalCode: '13890-070',
      price: 185_000,
      propertyType: 4,
      referenceSuffix: '0004',
      street: 'Rua Antônio Batista Sampaio',
      title: 'Quitinete compacta próxima à praça e com vaga',
      totalAreaSqm: 35,
    },
    {
      bathroomCount: 3,
      bedroomCount: 4,
      description:
        'Estrutura de lazer com churrasqueira coberta, redário e campo de futebol. Casa principal em alvenaria com 2 suítes; edícula para hóspedes. Estrada municipal asfaltada até o portão.',
      deterministicKey: 'dev-seed-imo-05-chacara',
      cityIndex: 1,
      imageUrls: SEED_CHACARA_IMAGE_URLS,
      parkingSpaceCount: 6,
      postalCode: '13870-000',
      price: 1_150_000,
      propertyType: 5,
      referenceSuffix: '0005',
      street: 'Estrada Vicinal Bairro dos Lopes',
      title: 'Chácara com pomar, canil e açude pequeno',
      totalAreaSqm: 6200,
    },
    {
      bathroomCount: 1,
      bedroomCount: 2,
      description:
        'Benfeitorias com casa simples, energia trifásica, poço artesiano com caixa elevatória. Pastagem formada, divisão de piquetes. Plano de manejo documentado. Sem fotos de exemplo no seed (cenário de catálogo sem imagem).',
      deterministicKey: 'dev-seed-imo-06-sitio',
      cityIndex: 2,
      imageUrls: [],
      parkingSpaceCount: 0,
      postalCode: '37710-000',
      price: 1_480_000,
      propertyType: 6,
      referenceSuffix: '0006',
      street: 'Estrada Municipal do Cruzeiro',
      title: 'Sítio com nascente preservada e curral',
      totalAreaSqm: 125_000,
    },
    {
      bathroomCount: 3,
      bedroomCount: 4,
      description:
        'Currais e brete completo, sede com 4 dormitórios, açude de 2 ha, silos metálicos. Solo argiloso com boa adensagem. ITR e certificação de reserva legal em dia.',
      deterministicKey: 'dev-seed-imo-07-fazenda',
      cityIndex: 1,
      imageUrls: SEED_FAZENDA_IMAGE_URLS,
      parkingSpaceCount: 4,
      postalCode: '13870-850',
      price: 8_200_000,
      propertyType: 7,
      referenceSuffix: '0007',
      street: 'Rodovia Euclides de Oliveira Sampaio, km 7',
      title: 'Fazenda dedicada a gado de corte e silagem',
      totalAreaSqm: 1_450_000,
    },
    {
      bathroomCount: null,
      bedroomCount: null,
      description:
        'Lote com testada para duas ruas, topografia plana, esgoto e água na meia-fachada. Matrícula com averbação de georreferenciamento. Frente: 30 m; lateral: 40 m. Sem edificação; contagens de dormitórios e vagas ausentes (terreno puro).',
      deterministicKey: 'dev-seed-imo-08-terreno',
      cityIndex: 0,
      imageUrls: SEED_TERRENO_IMAGE_URLS,
      parkingSpaceCount: null,
      postalCode: '13890-020',
      price: 310_000,
      propertyType: 8,
      referenceSuffix: '0008',
      street: 'Rua Doutor Armando Araújo',
      title: 'Terreno em esquina, zoneamento ZR-2',
      totalAreaSqm: 1200,
    },
    {
      bathroomCount: null,
      bedroomCount: null,
      description:
        'Área rural fora do perímetro urbano, registro com CAR, apta a plantio de soja 2ª safra. Acesso por estrada de terra, sem benfeitorias. CNPJ rural pode ser cedido à negociação.',
      deterministicKey: 'dev-seed-imo-09-gleba',
      cityIndex: 2,
      imageUrls: SEED_GLEBA_IMAGE_URLS,
      parkingSpaceCount: null,
      postalCode: '37704-000',
      price: 2_200_000,
      propertyType: 9,
      referenceSuffix: '0009',
      street: 'Acesso sem denominação oficial (trecho 3 km a partir da MG-184)',
      title: 'Gleba para cultivo e compensação legal',
      totalAreaSqm: 800_000,
    },
  ];

  return rows.map(buildRecord);
}
